import Vue from 'vue'
import VueResource from 'vue-resource'
import store from '../vuex/store'
import router from '../util/router'
import {API_HOST} from '../config'
import moment from 'moment'

var pjson = require('../../package.json')

Vue.use(VueResource)

Vue.http.options.root = API_HOST

// Intercept all API responses to check for 401 (Unauthorised)
Vue.http.interceptors.push((request, next) => {
  Vue.http.headers.common['Accept'] = 'application/json'

  // Vue.http.headers.common['Access-Control-Allow-Origin'] = '*'
  // Vue.http.headers.common['Access-Control-Allow-Headers'] = '*'
  // Vue.http.headers.common['processData'] = false
  // Vue.http.headers.common['Data-Type'] = 'json'
  // Vue.http.headers.common['Content-Type'] = 'application/json'

  Vue.http.headers.common['X-Charles-Version'] = pjson.version

  // Remove any previously set value for the modified header, as it persists
  delete Vue.http.headers.common['Authorization']
  delete Vue.http.headers.common['If-Modified-Since']

  if (store.state.accessToken) {
    Vue.http.headers.common['Authorization'] = 'Bearer ' + store.state.accessToken
  } else if (request.url !== 'oauth/token') {
    console.warn('Cancelling unauthenticated request', request.url)
    throw new Error('Cancelling unauthenticated request')
  }

  let isPaginationRequest = false

  // Generate a cache key for this specific resource
  let cacheKey = request.url.replace('{/id}', '') + Object.keys(request.params).map((k) => {
    // We need to ignore the 'page' paramater so that pagination requests do not have their
    // timestamps incorrectly overrided
    if (k === 'page' || k === 'limit') {
      isPaginationRequest = (k === 'page')
      return ''
    }
    return '_' + k + '_' + request.params[k]
  })
  cacheKey = cacheKey.replace(/,/g, '')

  if (request.method === 'GET') {
    let lastRequestTimestamp = store.state.requestTimestamps[cacheKey]
    let lastResponseTimestamp = store.state.responseTimestamps[cacheKey]

    if (!isPaginationRequest) {
      store.dispatch('setRequestTimestamp', {
        key: cacheKey,
        timestamp: moment().unix()
      })
    }

    if (lastResponseTimestamp !== undefined) {
      // if the last response was before the last request, then we are still waiting for a request from this
      // resource. Cancel the request if so
      const requestDiff = lastRequestTimestamp - lastResponseTimestamp
      if (lastRequestTimestamp !== undefined && requestDiff > 0 && requestDiff <= 30) { // Timeout wait for 30 seconds for existing requests to finish
        // Cancel the duplicate request
        console.warn('Cancelling duplicate request', cacheKey)
        throw new Error('Cancelling in flight request')
      }

      // We have a last response timestamp from this resource, make sure we only request items
      // that have changed since the last request
      Vue.http.headers.common['If-Modified-Since'] = moment.unix(lastResponseTimestamp).toISOString()
    }
  }

  next((response) => {
    if (!store.state.accessToken && request.url !== 'oauth/token') {
      console.warn('Cancelling unauthenticated response', request.url)
      throw new Error('Cancelling unauthenticated response')
    }

    if (response.status !== 200 && response.body && response.body.error) {
      console.error('SERVER: ' + response.body.error)
    }

    if (response.status === 401) { // Unauthorized - our token has expired
      // Remove our local copy of the token, forcing the user to log back in
      store.dispatch('logout')
      router.replace('/logout')
    }

    if (response.status === 200 || response.status === 304) {
      if (response.body &&
        response.body.current_page && // This is a paginated response.
        response.body.current_page < response.body.last_page) { // Check to see if we can expect more pages to come
        return // Do not store the response timestamp while waiting for more pages to come
      }

      // Cache the server response data for this resource so that we can
      // request items only updated since this date next time
      store.dispatch('setResponseTimestamp', {
        key: cacheKey,
        timestamp: moment(response.headers.Date).unix()
      })

      response.ok = true // Mark as True so that vue-resource-progressbar-interceptor doesn't show red for 304's
    }

    return response
  })
})

/**
 * API RESOURCES
 */
export const ClientsResource = Vue.resource('api/clients{/id}')
export const ProjectsResource = Vue.resource('api/projects{/id}')
export const TasksResource = Vue.resource('api/tasks{/id}')
export const LogsResource = Vue.resource('api/logs{/id}')
export const UsersResource = Vue.resource('api/users{/id}')
export const AppBuildsResource = Vue.resource('api/app-builds{/id}')
export const NotificationsResource = Vue.resource('api/notifications{/id}')
export const TagsResource = Vue.resource('api/tags{/id}')

export const AppBuildIconURL = API_HOST + '/api/app-builds/icon'
export const AppBuildIpaURL = API_HOST + '/api/app-builds/ipa'
export const AppBuildApkURL = API_HOST + '/api/app-builds/apk'
export const UsersIconURL = API_HOST + '/api/users/icon'
export const UsersWebPushSubscribeURL = API_HOST + '/api/users/web-push'
export const UsersWebPushUnsubscribeURL = API_HOST + '/api/users/web-push/unsubscribe'
export const ClientsIconURL = API_HOST + '/api/clients/icon'

/**
 * Recursively process GET requests using pagination
 *
 * @param resource - the VueResource to execute the GET request to
 * @param params - VueResource request parameters to send.
 * @param onSuccess - Required callback for success GET request. Will pass the response in the callback
 * @param onComplete - Optional callback for when all GET requests have complete
 * @param onError - Optional callback for error GET request. Will pass the error in the callback
 */
export const getWithPagination = (resource, params = {}, onSuccess, onComplete, onError) => {
  // Make sure a default pagination limit is set
  if (params.limit === undefined) {
    params.limit = 25
  }
  try {
    // used the commented code when testing the webapp local
    // params.headers = {
    //     'Access-Control-Allow-Origin': "*",
    //     'Access-Control-Allow-Headers': "*",
    //     'processData': false,
    //     'dataType': 'json',
    //     'contentType': 'application/json',
    //     'Authorization': 'Bearer ' + store.state.accessToken
    // }
    resource.get(params).then((response) => {
      // console.log('getWithPagination === res', response)
      // Immediately execute the callback with the reponse
      onSuccess(response)
      // If there are more pages to process, keep processing!
      if (response.body.current_page < response.body.last_page) {
        params.page = response.body.current_page + 1
        getWithPagination(resource, params, onSuccess, onError)
      } else {
        if (onComplete) onComplete()
      }
    }).catch(ex => {
      // console.log('error', ex)
      if (onError) onError(ex)
    })
  } catch (ex) {
    // console.log('error == ex', ex)
    if (onError) onError(ex)
  }
}
